<template>
  <div class="content">
    <h1 class="has-text-centered">Grenzfrequenz eines LC-Glieds berechnen</h1>

    <div class="has-text-centered" v-if="calculators.length > 1">
      <label class="label">Welche Formel möchtest du benutzen?</label>
      <div class="field pb-2">
        <div class="dropdown is-300px-width" :class="{ 'is-active': dropdown_active }">
          <div class="dropdown-trigger is-300px-width">
            <button class="button is-fullwidth" @click="toggleDropdown()">
              <span v-if="target_calculator.title != ''">{{ target_calculator.title }}</span>
              <span v-else v-katex="target_calculator.katex_formula"></span>
              <span class="icon is-small">
                <i class="fas fa-angle-down" aria-hidden="true"></i>
              </span>
            </button>
          </div>
          <div class="dropdown-menu is-300px-width">
            <div class="dropdown-content">
              <div class="dropdown-item is-clickable" v-for="(calculator, index) in calculators" :key="index" @click="selectTargetCalculator(index)" v-show="index != target_calculator_index">
                <p v-if="calculator.title != ''">{{ calculator.title }}</p>
                <p v-else v-katex="calculator.katex_formula"></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <DefaultCalculator v-for="(calculator, index) in calculators" :key="index" :prop_katex_formula="calculator.katex_formula" :prop_parser_formula="calculator.parser_formula" :prop_variables="calculator.variables" :prop_id="calculator.id" :prop_target_calculator_index="target_calculator_index" :prop_target_variable="getTargetVariable()" v-show="target_calculator_index == index" />

    <FeedbackBox />

    <GeneralAffiliateBox />

    <div class="box">
      <h2>Was ist ein LC-Glied?</h2>
      <p>
        LC-Glieder sind Schaltungen, die aus einer Spule (L) und einem Kondensator (C) aufgebaut sind.
      </p>
      <p>
        <font-awesome-icon icon="arrow-circle-right" />
        <a href="https://de.wikipedia.org/wiki/LC-Glied" target="_blank" class="pl-2">Wikipedia-Artikel</a>
      </p>

      <h2>Wie berechnet man die Grenzfrequenz eines LC-Glieds?</h2>
      <p>
        Die Grenzfrequenz eines LC-Glieds lässt sich über diese Formel berechnen:
      </p>
      <FormulaDescription v-for="(calculator, index) in calculators" :key="index" :katex_formula="calculator.katex_formula" :variables="calculator.variables" />

      <h2>Weiterführende Links</h2>
      <p>
        <ul class="fa-ul pb-3">
          <li>
            <span class="fa-li"><i class="fas fa-arrow-circle-right"></i></span>
            <a href="https://de.wikipedia.org/wiki/LC-Glied" target="_blank">Wikipedia-Artikel</a>
          </li>
          <li>
            <span class="fa-li"><i class="fas fa-arrow-circle-right"></i></span>
            <router-link :to="{name: 'calculator-rc-circuit-cutoff-freq'}">Grenzfrequenz eines RC-Glieds berechnen</router-link>
          </li>
        </ul>
      </p>
    </div>
  </div>
</template>

<script>
import DefaultCalculator from '@/components/DefaultCalculator.vue'
import FormulaDescription from '@/components/FormulaDescription.vue'
import FeedbackBox from '@/components/FeedbackBox.vue'
import GeneralAffiliateBox from '@/components/GeneralAffiliateBox.vue'

export default {
  name: 'calculator-lc-circuit-cutoff-freq',
  components: {
    DefaultCalculator,
    FormulaDescription,
    FeedbackBox,
    GeneralAffiliateBox
  },
  metaInfo: {
    title: 'Grenzfrequenz berechnen (LC-Glied)',
    meta: [
      {
        name: 'description',
        content: 'Mit diesem Rechner kannst du die Grenzfrequenz eines LC-Glieds berechnen ✓ Kreiszahl PI ✓ Induktivität ✓ Kapazität'
      }
    ]
  },
  created () {
    this.$store.commit('updateNavTitle', { title: '<span class="fas fa-angle-left"></span> Zurück', route: 'calculators' })
    this.target_calculator_index = this.getTargetCalculator()
  },
  methods: {
    toggleDropdown () {
      if (this.dropdown_active) {
        this.dropdown_active = false
      } else {
        this.dropdown_active = true
      }
    },
    selectTargetCalculator (index) {
      this.target_calculator_index = index
      this.dropdown_active = false
    },
    getValue (calcId, variable) {
      variable = variable.toLowerCase() + '_val'
      const reg = /^[+-]?[0-9]{1,16}(?:\.[0-9]{1,16})?$/
      if (parseInt(this.$route.query.calc, 10) === calcId) {
        for (const key in this.$route.query) {
          if (key === variable && reg.test(this.$route.query[key])) {
            return this.$route.query[key]
          }
        }
      }
      return 1
    },
    getMultiplicator (calcId, variable) {
      variable = variable.toLowerCase() + '_mul'
      const reg = /^[+-]?[0-9]{1,16}(?:\.[0-9]{1,16})?$/
      if (parseInt(this.$route.query.calc, 10) === calcId) {
        for (const key in this.$route.query) {
          if (key === variable && reg.test(this.$route.query[key])) {
            return Math.pow(10, this.$route.query[key])
          }
        }
      }
      return 1
    },
    getTargetVariable () {
      for (const i in this.calculators) {
        if (parseInt(this.$route.query.calc, 10) === this.calculators[i].id) {
          for (const key in this.$route.query) {
            if (key === 'target_var') {
              for (const y in this.calculators[i].variables) {
                if (this.calculators[i].variables[y].variable.toLowerCase() === this.$route.query[key]) {
                  return this.calculators[i].variables[y].label
                }
              }
            }
          }
        }
      }
      return null
    },
    getTargetCalculator () {
      for (const i in this.calculators) {
        if (parseInt(this.$route.query.calc, 10) === this.calculators[i].id) {
          return parseInt(i)
        }
      }
      return 0
    }
  },
  computed: {
    target_calculator: function () {
      return this.calculators[this.target_calculator_index]
    }
  },
  data () {
    return {
      dropdown_active: false,
      target_calculator_index: 0,
      calculators: [
        {
          id: 1,
          title: '',
          katex_formula: 'f_g = \\frac{1}{2 * \\pi * \\sqrt{L*C}}',
          parser_formula: 'f-(1/(2*pi*sqrt(L*C)))',
          variables: [
            {
              variable: 'f',
              unit: {
                content: 'Hz',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'f_g',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Grenzfrequenz in Hertz [Hz]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(1, 'f'),
                unitMultiplicator: this.getMultiplicator(1, 'f')
              },
              is_constant: false
            },
            {
              variable: 'pi',
              unit: {
                content: '',
                type: 'text',
                has_prefix: false
              },
              label: {
                short: [
                  {
                    content: '\\pi',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Kreiszahl PI von 3,1415926...',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: Math.PI,
                unitMultiplicator: 1
              },
              is_constant: true
            },
            {
              variable: 'L',
              unit: {
                content: 'H',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'L',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Induktivität in Henry [H]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(1, 'L'),
                unitMultiplicator: this.getMultiplicator(1, 'L')
              },
              is_constant: false
            },
            {
              variable: 'C',
              unit: {
                content: 'F',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'C',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Kapazität in Farad [F]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(1, 'C'),
                unitMultiplicator: this.getMultiplicator(1, 'C')
              },
              is_constant: false
            }
          ]
        }
      ]
    }
  }
}
</script>
